/**
 * Client identity: one Ed25519 key per browser profile. A user *is* their key
 * (the server derives the uid from the public key), so losing it means losing
 * group memberships such as admin.
 *
 * Storage decision: the key is persisted as a JWK in IndexedDB and imported
 * into WebCrypto as a *non-extractable* CryptoKey for signing at runtime. We
 * deliberately do not store a non-extractable CryptoKeyPair, because the
 * settings page must be able to export the identity (backup / move to
 * another device or the desktop app) and import one later. A non-extractable
 * key can never be exported, so that would make backups impossible. The cost:
 * script running on our origin can read the JWK from IndexedDB; that is the
 * same trust boundary as the page itself.
 */
import type { DeviceCertificate } from "../proto/DeviceCertificate";
import { decodeBase64Url, encodeBase64Url } from "./base64url";
import { kvDelete, kvEnroll, kvGet, kvSet, kvUpdate, type KvGuard } from "./kv";
import { defaultNickname, validNickname } from "./nickname";
import { NewerDataError, upgradeRecord, type RecordUpgrade } from "../lib/stored-data";

export interface Identity {
  readonly nickname: string;
  /**
   * Base64url (no padding) raw 32-byte Ed25519 public key: the one that signs
   * the challenge. For a Gwar Connect identity this is the *device* key.
   */
  readonly publicKey: string;
  /**
   * Gwar Connect: the account key's certificate for `publicKey`. Sent in
   * `hello`; the server then takes the uid from the certificate's account key.
   */
  readonly device?: DeviceCertificate;
  /** Signs UTF-8 bytes; returns base64url signature. */
  sign(message: Uint8Array): Promise<string>;
  /** Portable representation for backup. */
  exportBackup(): IdentityBackup;
}

export interface IdentityBackup {
  format: "gwar-identity";
  version: 1;
  jwk: JsonWebKey;
  nickname: string;
}

export interface IdentityStore {
  load(): Promise<IdentityBackup | undefined>;
  save(backup: IdentityBackup): Promise<void>;
}

export const KEY = "identity";
export const CONNECT_KEY = "connect";

/**
 * Upgrades of the stored records (and of identity backup files) as they are read
 * (docs/data-migrations.md): `IDENTITY_UPGRADES[i]` takes an identity from `version` i + 1 to
 * i + 2, `CONNECT_UPGRADES` likewise a Connect record. A record of a newer version fails to load
 * and stays as it is. Version 1 of each is the baseline.
 */
export const IDENTITY_UPGRADES: readonly RecordUpgrade[] = [];
export const CONNECT_UPGRADES: readonly RecordUpgrade[] = [];
export const identityRecord = (value: unknown) => upgradeRecord("The identity", value, IDENTITY_UPGRADES);
export const connectRecord = (value: unknown) => upgradeRecord("The Gwar Connect sign-in", value, CONNECT_UPGRADES);
// Checked in the transaction that replaces or deletes the record: a newer version's stays.
const keepNewerIdentity: KvGuard = (stored) => void identityRecord(stored);
const keepNewerConnect: KvGuard = (stored) => void connectRecord(stored);

export const indexedDbIdentityStore: IdentityStore = {
  load: async () => identityRecord(await kvGet<unknown>(KEY)) as IdentityBackup | undefined,
  save: (backup) => kvSet(KEY, backup, keepNewerIdentity),
};

/**
 * What a device keeps of a Gwar Connect account: its own key and certificate,
 * never the account key (that only exists in memory while signing in).
 * The device private key is stored as a JWK like the local identity's.
 * An unrelated local identity stays. A local account root is removed on enrollment.
 */
export interface ConnectRecord {
  version: 1;
  handle: string;
  /** Cached account profile; the encrypted vault is authoritative. */
  nickname?: string;
  /** Account public key (the uid is derived from it). */
  accountKey: string;
  deviceName: string;
  deviceJwk: JsonWebKey;
  certificate: DeviceCertificate;
  /** Device-bound session token (90 idle days, capped by its proven certificate); lists devices and reads and writes the vault. */
  token: string;
  /**
   * Opens the account's vault (base64url, docs/connect.md): derived from the
   * account key, so it can only be set by a flow that decrypts that key.
   */
  vaultKey: string;
  /**
   * A recovery archive key statement to publish (docs/e2e-build.md, section 7): signed at
   * sign-up, or by a recovery code change once Connect took the new code; the key archive
   * publishes it while the recovery credentials are still at `credential` (the version that
   * change left), and drops it otherwise.
   */
  archiveRecovery?: { statement: string; signature: string; credential: number };
  /** After a recovery-code sign-in: the recovery archive key (base64url), until the archive opened what it can with it. */
  archiveRecoverySecret?: string;
}

export interface ConnectStore {
  load(): Promise<ConnectRecord | undefined>;
  save(record: ConnectRecord): Promise<void>;
  clear(): Promise<void>;
}

export const indexedDbConnectStore: ConnectStore = {
  load: async () => connectRecord(await kvGet<unknown>(CONNECT_KEY)) as ConnectRecord | undefined,
  save: (record) => kvEnroll(CONNECT_KEY, record, record.accountKey, { record: keepNewerConnect, identity: keepNewerIdentity }),
  clear: () => kvDelete(CONNECT_KEY, keepNewerConnect),
};

/** Changes the stored Connect record in one transaction; `change` returns undefined to leave it. */
export function updateConnectRecord(change: (record: ConnectRecord | undefined) => ConnectRecord | undefined): Promise<void> {
  return kvUpdate<unknown>(CONNECT_KEY, (stored) => change(connectRecord(stored) as ConnectRecord | undefined));
}

/** A stored identity this version cannot read (damaged, or of a format it does not know): kept as it is, never replaced. */
export class IdentityUnreadableError extends Error {
  constructor() {
    super("The identity saved in this browser cannot be read by this version of Gwar. It was left as it is; update Gwar or restore it from a backup.");
    this.name = "IdentityUnreadableError";
  }
}

export class IdentityUnsupportedError extends Error {
  constructor() {
    super("Ed25519 is not available in this browser or context");
    this.name = "IdentityUnsupportedError";
  }
}

/** Why this device's identity did not load, as a connect failure (`err.connect.*`). */
export type IdentityFailure = "identity" | "identity_unreadable" | "identity_storage" | "storage_newer";
export function identityFailure(error: unknown): IdentityFailure {
  if (error instanceof IdentityUnsupportedError) return "identity";
  if (error instanceof IdentityUnreadableError) return "identity_unreadable";
  if (error instanceof NewerDataError) return "storage_newer";
  return "identity_storage";
}

const ALGORITHM = { name: "Ed25519" } as const;

function isValidBackup(value: unknown): value is IdentityBackup {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<IdentityBackup>;
  const jwk = v.jwk;
  return (
    v.format === "gwar-identity" &&
    v.version === 1 &&
    !!jwk &&
    jwk.kty === "OKP" &&
    jwk.crv === "Ed25519" &&
    typeof jwk.x === "string" &&
    typeof jwk.d === "string" &&
    validNickname(v.nickname) !== undefined
  );
}

function isValidConnectRecord(value: unknown): value is ConnectRecord {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<ConnectRecord>;
  const jwk = v.deviceJwk;
  const c = v.certificate;
  return (
    v.version === 1 &&
    typeof v.handle === "string" &&
    typeof v.accountKey === "string" &&
    typeof v.token === "string" &&
    typeof v.vaultKey === "string" &&
    !!jwk &&
    jwk.kty === "OKP" &&
    jwk.crv === "Ed25519" &&
    typeof jwk.x === "string" &&
    typeof jwk.d === "string" &&
    !!c &&
    c.account_key === v.accountKey &&
    c.device_key === jwk.x
  );
}

const deviceSeeds = new WeakMap<Identity, Uint8Array>();
/** The device seed for the MLS signer, without allowing a Connect device identity backup. */
export function identitySeed(identity: Identity): Uint8Array {
  const seed = deviceSeeds.get(identity);
  if (!seed) throw new Error("Device signing seed unavailable");
  return seed.slice();
}

/** Whether this browser can make Ed25519 keys at all: tells a key it rejects from an algorithm it lacks. */
async function ed25519Available(subtle: SubtleCrypto): Promise<boolean> {
  try {
    await subtle.generateKey(ALGORITHM, false, ["sign", "verify"]);
    return true;
  } catch {
    return false;
  }
}

/**
 * `invalid` is the error for key material this browser could use but rejects: by default a stored
 * identity that cannot be read (kept, never replaced).
 */
async function fromBackup(backup: IdentityBackup, device?: DeviceCertificate, invalid: () => Error = () => new IdentityUnreadableError()): Promise<Identity> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new IdentityUnsupportedError();
  let key: CryptoKey;
  try {
    key = await subtle.importKey("jwk", backup.jwk, ALGORITHM, false, ["sign"]);
  } catch {
    throw (await ed25519Available(subtle)) ? invalid() : new IdentityUnsupportedError();
  }
  const publicKey = backup.jwk.x as string;
  const identity: Identity = {
    publicKey,
    nickname: validNickname(backup.nickname) ?? defaultNickname(),
    device,
    async sign(message) {
      const sig = await subtle.sign(ALGORITHM, key, message as BufferSource);
      return encodeBase64Url(new Uint8Array(sig));
    },
    exportBackup() {
      // A device key is not the identity; exporting it would hand out a different uid.
      if (device) throw new Error("a Gwar Connect device has no portable identity backup");
      return { ...backup, jwk: { ...backup.jwk } };
    },
  };
  deviceSeeds.set(identity, decodeBase64Url(backup.jwk.d!));
  return identity;
}

/** The identity of a device signed in to Gwar Connect. */
export function identityFromConnect(record: ConnectRecord): Promise<Identity> {
  const { kty, crv, x, d } = record.deviceJwk;
  return fromBackup({ format: "gwar-identity", version: 1, jwk: { kty, crv, x, d }, nickname: record.nickname ?? defaultNickname() }, record.certificate);
}

/** The key the uid comes from: the account key when signed in to Connect, otherwise the identity key itself. */
export function accountKeyOf(identity: Identity): string {
  return identity.device?.account_key ?? identity.publicKey;
}

export async function generateIdentity(): Promise<Identity> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new IdentityUnsupportedError();
  let pair: CryptoKeyPair;
  try {
    pair = (await subtle.generateKey(ALGORITHM, true, ["sign", "verify"])) as CryptoKeyPair;
  } catch {
    throw new IdentityUnsupportedError();
  }
  const jwk = await subtle.exportKey("jwk", pair.privateKey);
  return fromBackup({ format: "gwar-identity", version: 1, jwk, nickname: defaultNickname() });
}

export async function loadOrCreateIdentity(store: IdentityStore = indexedDbIdentityStore): Promise<Identity> {
  const existing = await store.load();
  if (existing !== undefined) {
    // Never replaced by a new one: that would lose the uid and every role it holds.
    if (!isValidBackup(existing)) throw new IdentityUnreadableError();
    return fromBackup(existing);
  }
  const identity = await generateIdentity();
  await store.save(identity.exportBackup());
  return identity;
}

/**
 * The identity this device is using: the Gwar Connect one when signed in,
 * otherwise the local one (created on first use).
 */
export async function loadActiveIdentity(
  store: IdentityStore = indexedDbIdentityStore,
  connect: ConnectStore = indexedDbConnectStore,
): Promise<Identity> {
  // Not loadConnectRecord: a storage error must fail the load (and be retried whole), not read as
  // "signed out" and go on to create a local identity.
  const stored = await connect.load();
  const record = stored && isValidConnectRecord(stored) ? stored : undefined;
  if (record) {
    if (!validNickname(record.nickname)) {
      record.nickname = validNickname((await store.load())?.nickname) ?? defaultNickname();
      await connect.save(record);
    }
    return identityFromConnect(record);
  }
  return loadOrCreateIdentity(store);
}

/** The stored Connect sign-in, if any (and valid). */
export async function loadConnectRecord(connect: ConnectStore = indexedDbConnectStore): Promise<ConnectRecord | undefined> {
  // Unreadable storage reads as signed out, but a sign-in of a newer version must not: it would be
  // replaced by the next sign-in on this older version.
  const record = await connect.load().catch((error: unknown) => {
    if (error instanceof NewerDataError) throw error;
    return undefined;
  });
  return record && isValidConnectRecord(record) ? record : undefined;
}

/** Parses and installs an identity from a backup file's text. */
export async function importIdentity(text: string, store: IdentityStore = indexedDbIdentityStore): Promise<Identity> {
  let parsed: unknown;
  try {
    parsed = identityRecord(JSON.parse(text));
  } catch (error) {
    throw error instanceof Error && error.name === "NewerDataError" ? error : new Error("invalid identity file");
  }
  if (!isValidBackup(parsed)) throw new Error("invalid identity file");
  // Normalise to exactly the fields we need; drop key_ops etc.
  const { kty, crv, x, d } = parsed.jwk;
  const backup: IdentityBackup = { format: "gwar-identity", version: 1, jwk: { kty, crv, x, d }, nickname: validNickname(parsed.nickname)! };
  const identity = await fromBackup(backup, undefined, () => new Error("invalid identity file"));
  // Make sure the public half really belongs to the private half.
  const probe = new TextEncoder().encode("probe");
  const sig = decodeBase64Url(await identity.sign(probe));
  const pub = await crypto.subtle.importKey("raw", decodeBase64Url(x ?? "") as BufferSource, ALGORITHM, false, ["verify"]);
  if (!(await crypto.subtle.verify(ALGORITHM, pub, sig as BufferSource, probe))) throw new Error("invalid identity file");
  await store.save(backup);
  return identity;
}

/** Stable user id as the server derives it: base64url(SHA-256(raw public key)[..20]). */
export async function uidForPublicKey(publicKey: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", decodeBase64Url(publicKey) as BufferSource);
  return encodeBase64Url(new Uint8Array(digest).slice(0, 20));
}
